import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';

import {
  canCreateAndEditDraftExternalOrder,
  canDeleteDraftExternalOrder,
  canEditToComeAndInPreparationExternalOrder,
  canRevertExternalOrderToDraft,
  canRevertExternalOrderToInPreparation,
  canValidateDraftExternalOrder,
} from '@selectors/actions/externalOrderActions';
import { getAuthorizedActions } from '@selectors/featureProps';

import { downloadFileFromUrl, getFileNameFromUrl } from '@commons/utils/fileUtils';

import { externalOrder as externalOrderService } from '@services/externalOrder';

import {
  EXTERNAL_ORDER_PREPARED_TARGET_STATUS,
  EXTERNAL_ORDER_STATUS,
  EXTERNAL_ORDERS_PATH,
  PREPARED_EXTERNAL_ORDER_STATUSES,
} from '../constants';
import { getOrdersListReturnLocation } from '../utils/ordersListReturnLocation';
import ExternalOrderForm from '../components/ExternalOrderForm';
import PreparationExternalOrderForm from '../components/PreparationExternalOrderForm';
import ValidatedExternalOrderForm from '../components/ValidatedExternalOrderForm';

/**
 * The form builds a creation payload, which names the status the order must land on. The draft save
 * route takes none: it only ever saves a draft, a status transition being a route of its own
 * (COR-1685).
 */
const buildDraftUpdatePayload = ({ status, ...draftFields }) => draftFields;

/** A 409 on the delivery note means the order left the prepared status since the page was opened */
const CONFLICT_ERROR_STATUS = 409;

/**
 * Detail of an external order (/external/orders/:id/details). The status drives which form is
 * displayed: a draft is a form to fill in, a `A venir` order is read but for its comment, and from
 * `En préparation` onwards the BDL quantities take the stage — typed in while the preparation
 * lasts, frozen once the order is prepared.
 *
 * The order comes from GET /v2/b2b/external-orders/:id, which realigns a draft on the client catalog
 * before answering — so the form opens on the lines and the prices the order is actually worth.
 */
export const ExternalOrderDetails = (props) => {
  const {
    history,
    location,
    match: {
      params: { id },
    },
    canCreateAndEditDraft,
    canValidateDraft,
    canDeleteDraft,
    canRevertToDraft,
    canEditToComeAndInPreparation,
    canRevertToInPreparation,
    displaySuccessMessage,
    displayErrorMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const [externalOrder, setExternalOrder] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  // Back to the list as it was left — its tab, page and sort included.
  const goToList = () => {
    history.push(getOrdersListReturnLocation(location));
  };

  useEffect(() => {
    // A previously loaded order must not stay on screen while another one is being fetched: the
    // form would keep rendering — and editing — the one that is already gone.
    setExternalOrder(null);

    let isCurrentQuery = true;

    const fetchExternalOrder = async () => {
      pageLoading();

      try {
        const fetchedExternalOrder = await externalOrderService.getExternalOrder(id);

        if (!isCurrentQuery) {
          return;
        }

        setExternalOrder(fetchedExternalOrder);
      } catch {
        if (!isCurrentQuery) {
          return;
        }

        displayErrorMessage(i18next.t('EXTERNAL_ORDERS.FORM.FETCH_ERROR'));
        goToList();
      } finally {
        pageLoaded();
      }
    };

    fetchExternalOrder();

    return () => {
      isCurrentQuery = false;
    };
  }, [id, refreshIndex]);

  /**
   * Every action of the page ends with a toaster and a single flight at a time — so a double click
   * cannot fire two of them. Most actions land back on the list; the ones flagged `staysOnForm`
   * refetch the order instead, so the view of its new status takes over (COR-1830).
   */
  const submitOrder = async (
    submit,
    successMessage,
    errorMessage,
    { staysOnForm = false } = {},
  ) => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    pageLoading();

    try {
      await submit();

      displaySuccessMessage(successMessage);

      if (staysOnForm) {
        setRefreshIndex((currentIndex) => currentIndex + 1);
      } else {
        goToList();
      }
    } catch {
      displayErrorMessage(errorMessage);
    } finally {
      setIsSaving(false);
      pageLoaded();
    }
  };

  /**
   * Save the draft (PATCH, COR-1710). The lines sent replace the ones saved, so the order is written
   * exactly as the form shows it.
   */
  const handleSave = (payload) => {
    submitOrder(
      () => externalOrderService.updateDraftExternalOrder(id, buildDraftUpdatePayload(payload)),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_ERROR'),
    );
  };

  /**
   * Validate the draft into `A venir` (COR-1685). It is saved first: the button lights up as soon as
   * the draft is saveable, so whatever the user typed since must travel with the status change — the
   * validation route takes no body and would otherwise promote the lines as they were persisted.
   */
  const handleValidateDraft = (payload) => {
    submitOrder(
      async () => {
        await externalOrderService.updateDraftExternalOrder(id, buildDraftUpdatePayload(payload));
        await externalOrderService.validateExternalOrder(id);
      },
      i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.VALIDATE_DRAFT_ERROR'),
    );
  };

  const handleDelete = () => {
    submitOrder(
      () => externalOrderService.deleteExternalOrder(id),
      i18next.t('EXTERNAL_ORDERS.LIST.DELETE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.LIST.DELETE_ERROR'),
    );
  };

  /**
   * Move the delivery date and time of an order past the draft status (PATCH, COR-1771), along
   * with the pending comment and BDL quantities edited on the way — the delivery route only
   * carries the delivery itself. As every other save of the page, a success lands back on the
   * list, where the moved delivery shows up.
   */
  const handleSaveDelivery = ({ delivery, pendingComment, products }) => {
    submitOrder(
      async () => {
        if (pendingComment !== undefined) {
          await externalOrderService.updateExternalOrderComment(id, pendingComment);
        }

        if (products !== undefined) {
          await externalOrderService.updateExternalOrderPreparedQuantities(id, products);
        }

        await externalOrderService.updateExternalOrderDelivery(id, delivery);
      },
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_ERROR'),
    );
  };

  /**
   * Save the comment of an order past the draft status (PATCH, COR-1696), the only field these
   * statuses still let go of.
   */
  const handleSaveComment = (comment) => {
    submitOrder(
      () => externalOrderService.updateExternalOrderComment(id, comment),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_ERROR'),
    );
  };

  /**
   * Play a status transition (PATCH, COR-1686). A comment edited without being saved is written
   * first, and a pending delivery edition travels with the transition itself (COR-1771). Only the
   * transitions between `A venir` and `En préparation` keep the form open on the view of the new
   * status (COR-1830): every other transition lands back on the list.
   */
  const submitStatusTransition = ({
    status,
    pendingComment,
    delivery,
    successMessage,
    errorMessage,
    staysOnForm = false,
  }) => {
    submitOrder(
      async () => {
        if (pendingComment !== undefined) {
          await externalOrderService.updateExternalOrderComment(id, pendingComment);
        }

        await externalOrderService.updateExternalOrderStatus(id, status, delivery);
      },
      successMessage,
      errorMessage,
      { staysOnForm },
    );
  };

  const handleRevertToDraft = (pendingComment) => {
    submitStatusTransition({
      status: EXTERNAL_ORDER_STATUS.DRAFT,
      pendingComment,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_ERROR'),
    });
  };

  const handleStartPreparation = ({ pendingComment, delivery }) => {
    submitStatusTransition({
      status: EXTERNAL_ORDER_STATUS.IN_PREPARATION,
      pendingComment,
      delivery,
      successMessage: i18next.t('EXTERNAL_ORDERS.FORM.START_PREPARATION_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.FORM.START_PREPARATION_ERROR'),
      staysOnForm: true,
    });
  };

  /**
   * Save the BDL quantities and preparer ticks of an order in preparation (PATCH, COR-1697), along
   * with a comment edited on the way. Either half can stand alone — only what moved is written —
   * and the stock events of the order are regenerated server-side on the saved quantities.
   */
  const handleSavePreparation = ({ products, pendingComment }) => {
    submitOrder(
      async () => {
        if (pendingComment !== undefined) {
          await externalOrderService.updateExternalOrderComment(id, pendingComment);
        }

        if (products !== undefined) {
          await externalOrderService.updateExternalOrderPreparedQuantities(id, products);
        }
      },
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.SAVE_ERROR'),
    );
  };

  /**
   * Mark an order in preparation as prepared (PATCH, COR-1686). Pending quantities and comment are
   * saved first: the compliant / non compliant sub-state is resolved server-side from the persisted
   * BDL, which must be written as the user sees it before the transition freezes it — and a
   * prepared order would refuse the comment. A pending delivery edition travels with the transition
   * itself (COR-1771).
   */
  const handleMarkPrepared = ({ products, pendingComment, delivery }) => {
    submitOrder(
      async () => {
        if (pendingComment !== undefined) {
          await externalOrderService.updateExternalOrderComment(id, pendingComment);
        }

        if (products !== undefined) {
          await externalOrderService.updateExternalOrderPreparedQuantities(id, products);
        }

        await externalOrderService.updateExternalOrderStatus(
          id,
          EXTERNAL_ORDER_PREPARED_TARGET_STATUS,
          delivery,
        );
      },
      i18next.t('EXTERNAL_ORDERS.FORM.MARK_PREPARED_SUCCESS'),
      i18next.t('EXTERNAL_ORDERS.FORM.MARK_PREPARED_ERROR'),
    );
  };

  const handleRevertToToCome = (pendingComment) => {
    submitStatusTransition({
      status: EXTERNAL_ORDER_STATUS.TO_COME,
      pendingComment,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_ERROR'),
      staysOnForm: true,
    });
  };

  const handleRevertToInPreparation = () => {
    submitStatusTransition({
      status: EXTERNAL_ORDER_STATUS.IN_PREPARATION,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_ERROR'),
    });
  };

  /**
   * Generate and download the delivery note PDF of a prepared order (POST, COR-1728). The document
   * is regenerated on every click — the stored link only serves later re-downloads — and the page
   * stays open, unlike every other action: the download is the whole point. A 409 answer means the
   * order is not prepared anymore, so the form is resynchronized on its real status.
   */
  const handleDownloadDeliveryNote = useCallback(async () => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    pageLoading();

    try {
      const { deliveryNoteUrl } = await externalOrderService.generateExternalOrderDeliveryNote(id);

      await downloadFileFromUrl(deliveryNoteUrl, getFileNameFromUrl(deliveryNoteUrl));
    } catch (error) {
      displayErrorMessage(i18next.t('EXTERNAL_ORDERS.FORM.EXPORT_DELIVERY_NOTE_ERROR'));

      if (error?.response?.status === CONFLICT_ERROR_STATUS) {
        try {
          setExternalOrder(await externalOrderService.getExternalOrder(id));
        } catch {
          goToList();
        }
      }
    } finally {
      setIsSaving(false);
      pageLoaded();
    }
  }, [id, isSaving]);

  if (!externalOrder) {
    return null;
  }

  if (externalOrder.status === EXTERNAL_ORDER_STATUS.DRAFT) {
    return (
      <ExternalOrderForm
        canCreateAndEditDraft={canCreateAndEditDraft}
        canDeleteDraft={canDeleteDraft}
        canValidateDraft={canValidateDraft}
        externalOrder={externalOrder}
        isCreation={false}
        isSaving={isSaving}
        onClose={goToList}
        onDelete={handleDelete}
        onSave={handleSave}
        onValidateDraft={handleValidateDraft}
      />
    );
  }

  if (externalOrder.status === EXTERNAL_ORDER_STATUS.TO_COME) {
    return (
      <ValidatedExternalOrderForm
        canEditToComeAndInPreparation={canEditToComeAndInPreparation}
        canRevertToDraft={canRevertToDraft}
        externalOrder={externalOrder}
        isSaving={isSaving}
        onClose={goToList}
        onRevertToDraft={handleRevertToDraft}
        onSaveComment={handleSaveComment}
        onSaveDelivery={handleSaveDelivery}
        onStartPreparation={handleStartPreparation}
      />
    );
  }

  const isInPreparationOrPrepared =
    externalOrder.status === EXTERNAL_ORDER_STATUS.IN_PREPARATION ||
    PREPARED_EXTERNAL_ORDER_STATUSES.includes(externalOrder.status);

  if (isInPreparationOrPrepared) {
    return (
      <PreparationExternalOrderForm
        canEditToComeAndInPreparation={canEditToComeAndInPreparation}
        canRevertToInPreparation={canRevertToInPreparation}
        externalOrder={externalOrder}
        isSaving={isSaving}
        onClose={goToList}
        onDownloadDeliveryNote={handleDownloadDeliveryNote}
        onMarkPrepared={handleMarkPrepared}
        onRevertToInPreparation={handleRevertToInPreparation}
        onRevertToToCome={handleRevertToToCome}
        onSaveDelivery={handleSaveDelivery}
        onSavePreparation={handleSavePreparation}
      />
    );
  }

  return null;
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_ORDERS_PATH,
  );

  const isDeepsight = state.baseReducer.user.isDeepsight;

  return {
    canCreateAndEditDraft: isDeepsight || canCreateAndEditDraftExternalOrder(authorizedActions),
    canValidateDraft: isDeepsight || canValidateDraftExternalOrder(authorizedActions),
    canDeleteDraft: isDeepsight || canDeleteDraftExternalOrder(authorizedActions),
    canRevertToDraft: isDeepsight || canRevertExternalOrderToDraft(authorizedActions),
    canEditToComeAndInPreparation:
      isDeepsight || canEditToComeAndInPreparationExternalOrder(authorizedActions),
    canRevertToInPreparation:
      isDeepsight || canRevertExternalOrderToInPreparation(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  displaySuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalOrderDetails);
